// @ts-strict-ignore
import { trackBuildDistroViewEvent } from '@/actions/trackEvent';
import { createBuildDistroBuildRoute } from '@/helpers/routeHelpers';
import { BuildDistroBuild } from '@/types';
import { AppComponentProps } from '@/typescriptHelpers';
import { globalMessage } from '@/utils/globalMessage';
import { DownOutlined, LinkOutlined, QrcodeOutlined, ShareAltOutlined } from '@ant-design/icons';
import Space from 'antd/lib/space';
import { MenuProps } from 'antd/lib/menu';
import { MenuItemType } from 'antd/lib/menu/interface';
import * as React from 'react';
import { useDispatch } from 'react-redux';
import * as AnalyticEvents from '../../utils/analytics';
import { runwayColors } from '../styles/globalStyles';
import Dropdown from 'antd/lib/dropdown';
import Text from 'antd/lib/typography/Text';
import { QRCode } from 'react-qrcode-logo';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { UserPermissionScope } from '@/utils/hooks/useUserCanPerformAction';

interface BuildDistroShareMenuProps extends AppComponentProps {
    build: BuildDistroBuild;
}

enum ShareOptions {
    CopyURL,
    CopyQRCode,
    CopyPublicLink,
}

export const BuildDistroShareMenu: React.FC<BuildDistroShareMenuProps> = (props: BuildDistroShareMenuProps) => {
    const { orgId, appId, build } = props;
    const dispatch = useDispatch();
    const qrCodeRef = React.useRef(null);
    const url = `${window.location.origin}${createBuildDistroBuildRoute(orgId, appId, build.id, build.bucketId)}`;
    const isPublicBuild = build.publicBuildLinkEnabled;

    const makePublicBuildLink = (): string => {
        return `${window.location.origin}/bucket/${build.publicBucketHash}/build/${build.publicHash}`;
    };

    const onClickMenuItem = async (item) => {
        item.domEvent.stopPropagation();

        switch (parseInt(item.key)) {
            case ShareOptions.CopyQRCode: {
                const canvas = qrCodeRef.current?.canvas?.current;
                if (canvas) {
                    canvas.toBlob((blob) => {
                        navigator.clipboard.write([
                            new ClipboardItem({
                                'image/png': blob,
                            }),
                        ]);
                        globalMessage.success('QR code copied to clipboard!');
                    });
                }
                break;
            }
            case ShareOptions.CopyURL: {
                navigator.clipboard.writeText(url);
                globalMessage.success('Build link copied to clipboard!');
                break;
            }
            case ShareOptions.CopyPublicLink: {
                if (!isPublicBuild) {
                    return;
                }
                const link = makePublicBuildLink();
                navigator.clipboard.writeText(link);
                globalMessage.success('Build link copied to clipboard!');
                break;
            }
        }

        dispatch(
            trackBuildDistroViewEvent({
                orgId,
                appId,
                bucketId: build.bucketId,
                eventName: AnalyticEvents.Event.BuildDistroClickShareBuildCta,
            }),
        );
    };

    const menuItems: MenuItemType[] = [
        {
            label: (
                <div>
                    <Space align="center">
                        <LinkOutlined style={{ color: runwayColors.primary }} />
                        <Text>Copy {isPublicBuild ? 'internal ' : ''}link to clipboard</Text>
                    </Space>
                </div>
            ),
            key: ShareOptions.CopyURL,
            disabled: false,
        },
        isPublicBuild && {
            label: (
                <div>
                    <Space align="center">
                        <LinkOutlined style={{ color: runwayColors.primary }} />
                        <Text>Copy public link to clipboard</Text>
                    </Space>
                </div>
            ),
            key: ShareOptions.CopyPublicLink,
            disabled: false,
        },
        {
            label: (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 8, paddingTop: 8 }}>
                    <Space align="center">
                        <QrcodeOutlined style={{ color: runwayColors.primary }} />
                        <Text>Copy QR Code to clipboard</Text>
                    </Space>
                    <QRCode ref={qrCodeRef} value={url} size={280} />
                </div>
            ),
            key: ShareOptions.CopyQRCode,
        },
    ].filter(Boolean);

    const menuProps: MenuProps = {
        items: menuItems,
        onClick: onClickMenuItem,
    };

    return (
        <Dropdown menu={menuProps} trigger={['hover']} destroyPopupOnHide>
            <PermissionedActionButton
                block
                permissionScope={UserPermissionScope.Org}
                style={{ height: 'auto' }}
                icon={
                    <ShareAltOutlined
                        style={{
                            color: runwayColors.primary,
                        }}
                    />
                }
            >
                <Space style={{ marginLeft: 8 }}>
                    Share
                    <DownOutlined />
                </Space>
            </PermissionedActionButton>
        </Dropdown>
    );
};
